/**
 * Health Connect — staff-assisted health-data integration for a member.
 *
 * Staff pick a member, then connect that member's data sources (wearables,
 * Apple Health, EPIC clinical records, CARIN claims/EOB), see the resulting
 * observations, and request a (clinician-gated) AI insight summary. The member
 * still authenticates on the provider's own site at the authorize step; we never
 * see their provider password. Backed by the staff `/api/health/*` endpoints
 * (member_id is org-ownership-checked server-side).
 *
 * Note: members using the member portal get the self-service variant at
 * `/api/health/me/*`; this page is the staff/org view (member picker), which is
 * why it uses member_id-scoped endpoints rather than `/me`.
 */
import { useTabbedChrome } from '@/components/weavein';
import { useCallback, useEffect, useState } from 'react';
import {
  Activity, Watch, Heart, Stethoscope, Receipt, Wind, Smartphone,
  ExternalLink, Unlink, CheckCircle2, AlertCircle, Loader2, RefreshCw, Sparkles, LineChart,
} from 'lucide-react';
import { authFetch } from '@/lib/authFetch';
import { config } from '@/src/config';
import { MemberPicker, type PickedMember } from '@/components/behavioral-assessment/MemberPicker';

interface HealthConnectProps {
  organizationId?: number | string;
  organizationName?: string;
  userId?: number;
  /** When embedded in a member record, pin to this member and hide the picker. */
  fixedMemberId?: number;
  fixedMemberName?: string;
}

type Kind = 'wearable' | 'clinical' | 'claims' | 'environmental' | 'mobile';

interface SourceDef {
  provider: string;
  label: string;
  kind: Kind;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  oauth: boolean;
  fhirBaseDefault?: string;
}

interface Connection {
  id: string;
  provider: string;
  kind: string;
  status: string;
  last_sync_at?: string | null;
  error_count?: number;
  last_error?: string | null;
}

interface Observation {
  source_provider?: string;
  category?: string;
  code?: string;
  display?: string;
  value_num?: number | null;
  value_text?: string | null;
  unit?: string | null;
  effective_time?: string;
}

const SECTIONS: { title: string; blurb: string; sources: SourceDef[] }[] = [
  {
    title: 'Wearables & devices',
    blurb: 'Activity, heart rate, and sleep from the member’s device.',
    sources: [
      { provider: 'fitbit', label: 'Fitbit', kind: 'wearable', icon: Watch, oauth: true, description: 'Steps, resting heart rate, sleep.' },
      { provider: 'oura', label: 'Oura', kind: 'wearable', icon: Activity, oauth: true, description: 'Daily activity and sleep scores.' },
      { provider: 'garmin', label: 'Garmin', kind: 'wearable', icon: Activity, oauth: true, description: 'Steps and resting heart rate.' },
      { provider: 'google_fit', label: 'Google Fit', kind: 'wearable', icon: Activity, oauth: true, description: 'Daily step counts.' },
      { provider: 'apple_health', label: 'Apple Health', kind: 'mobile', icon: Smartphone, oauth: false, description: 'Connected from the WeaveIn mobile app (Health / Health Connect).' },
    ],
  },
  {
    title: 'Clinical records',
    blurb: 'The member’s medical record from their health system.',
    sources: [
      { provider: 'epic', label: 'EPIC (MyChart)', kind: 'clinical', icon: Stethoscope, oauth: true, description: 'Conditions, medications, allergies, care plans, vitals/labs.', fhirBaseDefault: 'https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4' },
    ],
  },
  {
    title: 'Health plan & claims',
    blurb: 'Explanation of Benefits (EOB) and claims from the member’s payer.',
    sources: [
      { provider: 'carin_bb', label: 'Health plan (Blue Button)', kind: 'claims', icon: Receipt, oauth: true, description: 'EOBs and claims via CARIN Blue Button.', fhirBaseDefault: 'https://sandbox.bluebutton.cms.gov/v2/fhir' },
    ],
  },
  {
    title: 'Environmental',
    blurb: 'Local air-quality readings for the member’s area.',
    sources: [
      { provider: 'environmental', label: 'Air quality', kind: 'environmental', icon: Wind, oauth: false, description: 'Automatic, based on location — no sign-in needed.' },
    ],
  },
];

export function HealthConnect({ organizationName, fixedMemberId, fixedMemberName }: HealthConnectProps) {
  const tabbed = useTabbedChrome();
  const base = config.weaveinApi.baseUrl;
  const [member, setMember] = useState<PickedMember | null>(
    fixedMemberId ? ({ id: fixedMemberId, name: fixedMemberName || `Member ${fixedMemberId}` } as PickedMember) : null,
  );
  const [connections, setConnections] = useState<Connection[]>([]);
  const [observations, setObservations] = useState<Observation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fhirBase, setFhirBase] = useState<Record<string, string>>({});
  const [insightBusy, setInsightBusy] = useState(false);

  const memberId = member?.id ?? null;

  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      setError(null);
      const [connResp, obsResp] = await Promise.all([
        authFetch(`${base}/api/health/connections?member_id=${memberId}`),
        authFetch(`${base}/api/health/members/${memberId}/observations`),
      ]);
      if (connResp.status === 403) {
        setError('Deep health data integration is not enabled for this organization.');
        setConnections([]);
        setObservations([]);
        return;
      }
      if (!connResp.ok) throw new Error(`Failed to load connections (${connResp.status})`);
      const connData = await connResp.json();
      setConnections(connData.connections || []);
      if (obsResp.ok) {
        const obsData = await obsResp.json();
        setObservations(obsData.observations || (Array.isArray(obsData) ? obsData : []));
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, [base, memberId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFocus = () => { if (memberId) load(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load, memberId]);

  const connectionFor = (provider: string) =>
    connections.find((c) => c.provider === provider && c.status !== 'revoked');

  const startConnect = async (src: SourceDef) => {
    if (!memberId) return;
    setBusy(src.provider);
    setNotice(null);
    try {
      const params = new URLSearchParams({ member_id: String(memberId), redirect_after: window.location.href });
      if (src.fhirBaseDefault) {
        const b = (fhirBase[src.provider] || src.fhirBaseDefault).trim();
        if (!b) { setNotice(`Enter the FHIR base URL for ${src.label} first.`); return; }
        params.set('fhir_base_url', b);
      }
      const resp = await authFetch(`${base}/api/health/connect/${src.provider}/start?${params.toString()}`);
      if (resp.status === 503) {
        setNotice(`${src.label} isn’t configured yet — an administrator must register the OAuth app and add its client credentials.`);
        return;
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Could not start ${src.label} (${resp.status})`);
      }
      const data = await resp.json();
      if (data.authorize_url) {
        window.location.href = data.authorize_url;
      } else {
        setNotice(`Unexpected response starting ${src.label}.`);
      }
    } catch (e: any) {
      setNotice(e?.message || `Could not connect ${src.label}`);
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (conn: Connection, label: string) => {
    setBusy(conn.provider);
    setNotice(null);
    try {
      const resp = await authFetch(`${base}/api/health/connections/${conn.id}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error(`Could not disconnect ${label} (${resp.status})`);
      await load();
      setNotice(`Disconnected ${label}.`);
    } catch (e: any) {
      setNotice(e?.message || `Could not disconnect ${label}`);
    } finally {
      setBusy(null);
    }
  };

  const requestInsight = async () => {
    if (!memberId) return;
    setInsightBusy(true);
    setNotice(null);
    try {
      const resp = await authFetch(`${base}/api/health/members/${memberId}/insight`, { method: 'POST' });
      if (!resp.ok) throw new Error(`Could not request insight (${resp.status})`);
      setNotice('Insight requested — it’s parked for clinician review in the member’s record.');
    } catch (e: any) {
      setNotice(e?.message || 'Could not request insight');
    } finally {
      setInsightBusy(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <div className="mb-6">
        {!tabbed && (
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2">
          <Heart className="w-6 h-6 text-primary" /> Health Connect
        </h1>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Connect a member’s own health accounts{organizationName ? ` for ${organizationName}` : ''} and review the resulting
          observations. The member signs in on each provider’s site — we never see their password.
        </p>
      </div>

      {/* Member picker (staff-assisted) — hidden when embedded in a member record */}
      {!fixedMemberId && (
        <div className="mb-6 rounded-lg border border-v2border bg-surface p-4">
          <label className="mb-1 block text-sm font-medium text-ink">Member</label>
          <MemberPicker value={member} onChange={setMember} />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Pick the member whose health sources you’re connecting or reviewing.</p>
        </div>
      )}

      {!member ? (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300">
          <AlertCircle className="w-4 h-4" /> Select a member to view and connect their health data.
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {notice && (
            <div className="mb-4 flex items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300">
              <span>{notice}</span>
              <button onClick={() => setNotice(null)} className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">✕</button>
            </div>
          )}

          <div className="mb-4 flex justify-end">
            <button onClick={() => load()} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="space-y-8">
              {SECTIONS.map((section) => (
                <section key={section.title}>
                  <h2 className="text-base font-medium text-ink">{section.title}</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{section.blurb}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {section.sources.map((src) => {
                      const conn = connectionFor(src.provider);
                      const Icon = src.icon;
                      const isBusy = busy === src.provider;
                      return (
                        <div key={src.provider} className="rounded-lg border border-v2border bg-surface p-4 flex flex-col gap-3">
                          <div className="flex items-start gap-3">
                            <div className="rounded-md bg-gray-100 dark:bg-gray-800 p-2"><Icon className="w-5 h-5 text-primary" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-ink">{src.label}</span>
                                <StatusBadge conn={conn} />
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{src.description}</p>
                              {conn?.last_sync_at && (<p className="text-[11px] text-gray-400 mt-1">Last synced {new Date(conn.last_sync_at).toLocaleString()}</p>)}
                              {conn?.status === 'error' && conn?.last_error && (<p className="text-[11px] text-red-500 mt-1 truncate">Error: {conn.last_error}</p>)}
                            </div>
                          </div>

                          {src.fhirBaseDefault && !conn && (
                            <input
                              type="text"
                              value={fhirBase[src.provider] ?? src.fhirBaseDefault}
                              onChange={(e) => setFhirBase((p) => ({ ...p, [src.provider]: e.target.value }))}
                              placeholder="FHIR base URL"
                              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs"
                            />
                          )}

                          <div className="mt-auto">
                            {!src.oauth ? (
                              <span className="text-xs text-gray-400">{src.kind === 'mobile' ? 'Via the mobile app' : 'Automatic'}</span>
                            ) : conn ? (
                              <button onClick={() => disconnect(conn, src.label)} disabled={isBusy}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50">
                                {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />} Disconnect
                              </button>
                            ) : (
                              <button onClick={() => startConnect(src)} disabled={isBusy}
                                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:brightness-90 disabled:opacity-50">
                                {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} Connect
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}

              {/* Results & workflows */}
              <section>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-base font-medium text-ink flex items-center gap-2"><LineChart className="w-4 h-4 text-primary" /> Observations &amp; insights</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Ingested readings from connected sources, plus a clinician-gated AI summary.</p>
                  </div>
                  <button onClick={requestInsight} disabled={insightBusy}
                    className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 disabled:opacity-50">
                    {insightBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Request AI insight
                  </button>
                </div>
                {observations.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                    No observations yet. Connect a source above; readings appear here after the next sync.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-v2border">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-v2border text-left text-gray-500 dark:text-gray-400">
                          <th className="px-4 py-2 font-medium">Type</th>
                          <th className="px-4 py-2 font-medium text-right">Value</th>
                          <th className="px-4 py-2 font-medium">Source</th>
                          <th className="px-4 py-2 font-medium">Observed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {observations.slice(0, 50).map((o, i) => (
                          <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="px-4 py-2">{o.display || o.code || '—'}</td>
                            <td className="px-4 py-2 text-right tabular-nums">{o.value_num ?? o.value_text ?? '—'}{o.unit ? ` ${o.unit}` : ''}</td>
                            <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{o.source_provider || '—'}</td>
                            <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{o.effective_time ? new Date(o.effective_time).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatusBadge({ conn }: { conn?: Connection }) {
  if (!conn) return <span className="text-[11px] rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 px-2 py-0.5">Not connected</span>;
  if (conn.status === 'active') return <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300 px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Connected</span>;
  if (conn.status === 'error') return <span className="text-[11px] rounded-full bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300 px-2 py-0.5">Error</span>;
  return <span className="text-[11px] rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 px-2 py-0.5">{conn.status}</span>;
}

export default HealthConnect;
